// MF-67 — Create/Edit Journal Entry. Pick a template → its fields render dynamically; add the
// students involved via the roster picker (each with a role), set when it occurred + location, and
// save. Type (from the category), severity (or none) and points (from the template) are INHERITED and
// read-only (to record
// a different level, pick another template). Supports a pre-selected
// student via query params (studentId/code/name/yearLevel/house/form) so a "+ Journal entry"
// action from a student profile can deep-link here with the student filled in.
// Layout: the form on the left, a sticky summary on the right with what's still missing + Save.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { Autocomplete, Box, Button, CircularProgress, TextField, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Module from '../../../layout/Module'
import FormCard from '../components/FormCard'
import FormField from '../components/FormField'
import FormSection from '../components/FormSection'
import FieldInput, { isFieldEmpty } from '../components/FieldInput'
import FileUpload from '../components/FileUpload'
import StudentPicker from '../components/StudentPicker'
import UserPicker from '../components/UserPicker'
import InheritedLevel from '../components/InheritedLevel'
import LocationField from '../components/LocationField'
import ColorBadge, { Points, TypeBadge } from '../components/ColorBadge'
import { inputSx, twoColumnSx } from '../components/formStyles'
import { useSeveritiesMap, useTypes } from '../components/useLookups'
import { getTemplates, getTemplate } from '../../../../services/journal/templates.service'
import { createEntry, getEntry, updateEntry } from '../../../../services/journal/entries.service'

const pad = (n) => String(n).padStart(2, '0')
// "YYYY-MM-DDTHH:mm" (local) for the datetime-local input.
const toLocal = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
// Staff usually log an event that just happened, so a new entry defaults to now (still editable).
const nowLocal = () => toLocal(new Date())

const SummaryRow = ({ label, children }) => (
  <div className='flex items-start justify-between' style={{ gap: 12 }}>
    <span style={{ fontSize: 13, color: 'rgb(100, 116, 139)', flex: 'none' }}>{label}</span>
    <span style={{ fontSize: 13, color: 'rgb(15, 23, 42)', textAlign: 'right', minWidth: 0 }}>
      {children}
    </span>
  </div>
)

const Todo = ({ done, children }) => (
  <div
    className='flex items-center'
    style={{ gap: 8, fontSize: 13, color: done ? 'rgb(100, 116, 139)' : 'rgb(30, 41, 59)' }}
  >
    <FontAwesomeIcon
      icon={done ? ['fal', 'check'] : ['fal', 'circle']}
      style={{ width: 14, color: done ? 'rgb(22, 163, 74)' : 'rgb(148, 163, 184)' }}
    />
    <span>{children}</span>
  </div>
)

const EntryForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const { enqueueSnackbar } = useSnackbar()
  const severities = useSeveritiesMap()
  const { byCode: typesByCode } = useTypes()
  const currentUser = useSelector((state) => state?.session?.user || {})
  const [loadingData, setLoadingData] = useState(Boolean(id))
  // When loading an existing entry we set templateId, which triggers the template-load effect. Skip
  // that effect's field/severity RESET once, so the values we loaded from the entry aren't wiped.
  const skipTemplateResetRef = useRef(false)

  // Reported by: defaults to the logged-in user (the common case), but staff can change/add others.
  const [reporters, setReporters] = useState(() =>
    currentUser?.userId || currentUser?.id
      ? [
          {
            userId: currentUser.userId || currentUser.id,
            code: currentUser.code || '',
            name: currentUser.fullName || ''
          }
        ]
      : []
  )
  const [templates, setTemplates] = useState([])
  const [templateId, setTemplateId] = useState('')
  const [template, setTemplate] = useState(null)
  const [fieldValues, setFieldValues] = useState({})
  // Pre-select a student when deep-linked from a profile (?studentId=&code=&name=…).
  const [students, setStudents] = useState(() => {
    const studentId = searchParams.get('studentId')
    if (!studentId) return []
    return [
      {
        userId: studentId,
        code: searchParams.get('code') || '',
        name: searchParams.get('name') || '',
        yearLevel: searchParams.get('yearLevel') || '',
        house: searchParams.get('house') || '',
        formGroup: searchParams.get('form') || '',
        role: 'involved'
      }
    ]
  })
  const [overview, setOverview] = useState('')
  // Files attached to the entry itself (always available, whatever the template).
  const [attachments, setAttachments] = useState([])
  const [location, setLocation] = useState('')
  const [zoneId, setZoneId] = useState(null)
  const [geo, setGeo] = useState({ lat: null, lng: null })
  const [occurredAt, setOccurredAt] = useState(nowLocal)
  // Edit mode: the level the entry was recorded with (kept unless the template changes).
  const [recorded, setRecorded] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getTemplates({ limit: 500 })
      .then(({ data }) => {
        const rows = data?.paginatedResults ?? []
        // Group by category in the picker: sort by category name, then title.
        rows.sort(
          (a, b) =>
            (a.category?.name || '').localeCompare(b.category?.name || '') ||
            (a.title || '').localeCompare(b.title || '')
        )
        setTemplates(rows)
      })
      .catch(() => setTemplates([]))
  }, [])

  // Edit mode: load the existing entry and prefill every field.
  useEffect(() => {
    if (!id) return
    getEntry(id)
      .then(({ data }) => {
        if (!data) return
        setOverview(data.overview || '')
        setLocation(data.location || '')
        setZoneId(data.zoneId || null)
        setGeo(data.geo || { lat: null, lng: null })
        setRecorded({
          templateId: String(data.template?._id || ''),
          severity: data.severity,
          type: data.type || 'neutral',
          points: data.points ?? 0
        })
        setOccurredAt(data.occurredAt ? toLocal(new Date(data.occurredAt)) : nowLocal())
        setReporters(data.reporters || [])
        setStudents(data.students || [])
        setAttachments(Array.isArray(data.attachments) ? data.attachments : [])
        setFieldValues(
          (data.fieldValues || []).reduce((acc, f) => {
            if (f.fieldId != null) acc[f.fieldId] = f.value
            return acc
          }, {})
        )
        // Set the template last; skip the reset so the values above survive.
        skipTemplateResetRef.current = true
        setTemplateId(String(data.template?._id || ''))
      })
      .catch(() => {})
      .finally(() => setLoadingData(false))
  }, [id])

  useEffect(() => {
    if (!templateId) {
      setTemplate(null)
      return
    }
    getTemplate(templateId)
      .then(({ data }) => {
        setTemplate(data)
        // On a fresh user template pick, clear field values. When we just loaded an existing entry,
        // keep what we loaded.
        if (skipTemplateResetRef.current) {
          skipTemplateResetRef.current = false
        } else {
          setFieldValues({})
        }
      })
      .catch(() => setTemplate(null))
  }, [templateId])

  const fields = useMemo(() => template?.fields ?? [], [template])
  const missingRequired = fields.filter((f) => f.required && isFieldEmpty(f, fieldValues[f.id]))
  const selectedTemplate = templates.find((t) => t._id === templateId) || null
  // The level shown (read-only): what the entry was recorded with while its template is unchanged,
  // otherwise what the chosen template gives (type from its category; severity or none + points).
  const level = useMemo(() => {
    const sev = (code) => {
      if (!code) return null
      const r = severities.get(code)
      return { label: r?.label || code, color: r?.color || '#94A3B8' }
    }
    // Severity only exists for types graded by it (e.g. Negative); otherwise it is "not applicable".
    const applies = (type) => Boolean(typesByCode.get(type)?.usesSeverity)
    if (recorded && recorded.templateId === templateId) {
      const severityApplies = applies(recorded.type)
      return {
        type: recorded.type,
        severity: severityApplies ? sev(recorded.severity) : null,
        severityApplies,
        points: recorded.points
      }
    }
    if (!template) return null
    const type = selectedTemplate?.category?.type || 'neutral'
    const severityApplies = applies(type)
    return {
      type,
      severity: severityApplies ? sev(template.defaults?.severity) : null,
      severityApplies,
      points: template.defaults?.points ?? 0
    }
  }, [recorded, templateId, template, selectedTemplate, severities, typesByCode])

  // Required template fields block NEW entries only: imported history may lack them and must stay editable.
  const requiredOk = Boolean(id) || missingRequired.length === 0
  const canSave = templateId && occurredAt && students.length > 0 && requiredOk && !saving

  const onSave = async () => {
    setSaving(true)
    try {
      const payload = {
        templateId,
        overview,
        occurredAt: new Date(occurredAt).toISOString(),
        location,
        zoneId,
        geo,
        reporters,
        students,
        attachments,
        fieldValues: fields.map((f) => ({
          fieldId: f.id,
          label: f.label,
          value: fieldValues[f.id] ?? null
        }))
      }
      if (id) await updateEntry(id, payload)
      else await createEntry(payload)
      enqueueSnackbar(id ? 'Journal entry updated' : 'Journal entry created', {
        variant: 'success'
      })
      navigate('/dashboard/journal/entries')
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error?.description || 'Could not save entry', {
        variant: 'error'
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Module>
      <Module.Header
        moduleName='journalentries'
        title={id ? 'Edit Journal Entry' : 'New Journal Entry'}
        titleLink='/dashboard/journal/entries'
      />
      <Module.BodyContainer className='overflow-y-auto'>
        {loadingData ? (
          <div className='flex justify-center' style={{ padding: '40px 0' }}>
            <CircularProgress />
          </div>
        ) : (
          <Box
            className='grid items-start'
            style={{ gap: 24, padding: '24px 8px', maxWidth: 1200, margin: '0 auto' }}
            sx={twoColumnSx}
          >
            <FormCard>
              <FormSection first title='What happened'>
                <FormField
                  label='Template'
                  required
                  htmlFor='entry-template'
                  hint={
                    templates.length === 0 ? 'No templates yet — create one under Journal · Templates.' : ''
                  }
                >
                  <Autocomplete
                    id='entry-template'
                    size='small'
                    options={templates}
                    groupBy={(o) => o.category?.name || 'Other'}
                    getOptionLabel={(o) => o?.title || ''}
                    isOptionEqualToValue={(o, v) => o._id === v._id}
                    value={selectedTemplate}
                    onChange={(_, tpl) => setTemplateId(tpl?._id || '')}
                    noOptionsText='No templates match'
                    renderInput={(params) => (
                      <TextField {...params} placeholder='Search the type of entry…' sx={inputSx} />
                    )}
                  />
                </FormField>

                <Box
                  className='grid'
                  style={{ gap: 20 }}
                  sx={{ gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}
                >
                  <FormField label='Date occurred' required htmlFor='entry-occurred'>
                    <TextField
                      id='entry-occurred'
                      size='small'
                      fullWidth
                      type='datetime-local'
                      value={occurredAt}
                      onChange={(e) => setOccurredAt(e.target.value)}
                      sx={inputSx}
                    />
                  </FormField>
                </Box>

                <FormField
                  label='Level'
                  help='Category, type, severity and points all come from the template. To record a different level, choose another template.'
                >
                  <InheritedLevel
                    category={selectedTemplate?.category}
                    level={level}
                    source='the template'
                    emptyText='Choose a template to see its type, severity and points.'
                  />
                </FormField>

                <FormField
                  label='Overview'
                  htmlFor='entry-overview'
                  hint='A short headline, shown in lists and reports.'
                >
                  <TextField
                    id='entry-overview'
                    size='small'
                    fullWidth
                    placeholder='e.g. Helped a classmate during recess'
                    value={overview}
                    onChange={(e) => setOverview(e.target.value)}
                    sx={inputSx}
                  />
                </FormField>
              </FormSection>

              {fields.length > 0 ? (
                <FormSection
                  title='Details'
                  description={`Questions from the “${template?.title}” template.`}
                >
                  {fields.map((field) => (
                    <FieldInput
                      key={field.id}
                      field={field}
                      value={fieldValues[field.id]}
                      onChange={(v) => setFieldValues((prev) => ({ ...prev, [field.id]: v }))}
                    />
                  ))}
                </FormSection>
              ) : null}

              <FormSection title='Students' description='Who the entry is about. Set each student’s role.'>
                <StudentPicker value={students} onChange={setStudents} />
              </FormSection>

              <FormSection title='Where'>
                <LocationField
                  value={{ location, zoneId, geo }}
                  onChange={(v) => {
                    setLocation(v.location || '')
                    setZoneId(v.zoneId || null)
                    setGeo(v.geo || { lat: null, lng: null })
                  }}
                />
              </FormSection>

              <FormSection
                title='Reported by'
                description='Who reported the event — it can differ from who records it. Defaults to you.'
              >
                <UserPicker value={reporters} onChange={setReporters} />
              </FormSection>

              <FormSection
                title='Attachments'
                description='Optional. Add a document, a photo of the event, or anything that supports the entry.'
              >
                <FileUpload kind='file' value={attachments} onChange={setAttachments} />
              </FormSection>
            </FormCard>

            <FormCard
              title='Summary'
              style={{ position: 'sticky', top: 16 }}
              footer={
                <>
                  <Button color='inherit' onClick={() => navigate('/dashboard/journal/entries')}>
                    Cancel
                  </Button>
                  <Button variant='contained' disabled={!canSave} onClick={onSave}>
                    {saving ? 'Saving…' : id ? 'Save changes' : 'Create entry'}
                  </Button>
                </>
              }
            >
              <div className='flex flex-col' style={{ gap: 10 }}>
                <SummaryRow label='Template'>{selectedTemplate?.title || '—'}</SummaryRow>
                <SummaryRow label='Category'>{selectedTemplate?.category?.name || '—'}</SummaryRow>
                <SummaryRow label='Type'>
                  {level ? <TypeBadge code={level.type || 'neutral'} /> : '—'}
                </SummaryRow>
                <SummaryRow label='Severity'>
                  {!level ? (
                    '—'
                  ) : level.severity ? (
                    <ColorBadge label={level.severity.label} color={level.severity.color} size='sm' />
                  ) : level.severityApplies ? (
                    'Not set'
                  ) : (
                    'Not applicable'
                  )}
                </SummaryRow>
                <SummaryRow label='Points'>{level ? <Points value={level.points ?? 0} /> : '—'}</SummaryRow>
                <SummaryRow label='Students'>
                  {students.length ? students.map((s) => s.name).join(', ') : '—'}
                </SummaryRow>
              </div>
              <div
                className='flex flex-col'
                style={{ gap: 8, borderTop: '1px solid rgb(241, 245, 249)', paddingTop: 16 }}
              >
                <Typography style={{ fontSize: 12, fontWeight: 600, color: 'rgb(71, 85, 105)' }}>
                  {canSave || saving ? 'Ready to save' : 'Before you save'}
                </Typography>
                <Todo done={Boolean(templateId)}>Choose a template</Todo>
                <Todo done={Boolean(occurredAt)}>Set when it happened</Todo>
                <Todo done={students.length > 0}>Add at least one student</Todo>
                {fields.some((f) => f.required) ? (
                  <Todo done={missingRequired.length === 0}>
                    Fill the required details
                    {missingRequired.length ? ` (${missingRequired.map((f) => f.label).join(', ')})` : ''}
                  </Todo>
                ) : null}
              </div>
            </FormCard>
          </Box>
        )}
      </Module.BodyContainer>
    </Module>
  )
}

export default EntryForm
